import { CHROME_EXTENSION_URL } from '@flexbyt/shared';
import { SourceGlyph, cx } from '@flexbyt/widget-preview';
import type { PointerEvent, ReactNode } from 'react';
import { sourcePagePath } from '../../content/integrations.js';
import { CONNECT_SOURCES, EXTENSION_SOURCES } from '../../content/sources.js';
import { SiteLink } from '../ui/button.js';
import { Heading, Text } from '../ui/type.js';

/**
 * Where reviews can come from.
 *
 * ===========================================================================
 * One job, and only one
 * ===========================================================================
 * This section exists to make a visitor think *"it works with the place my
 * reviews already are"* and then move on. It is not a feature section, not a
 * comparison, and not another opportunity to show review cards.
 *
 * So the tiles carry a mark and a name and nothing else. Five bordered cards
 * each holding a logo, a heading and two lines of benefit copy is the
 * generic-template shape the brief rules out, and it would also make this
 * section compete with the Import section further down, which is the one that
 * actually explains connecting a source. A logo wall is a different thing from
 * a feature grid, and the difference is exactly that: no prose inside the box.
 *
 * ===========================================================================
 * Why tiles rather than a row of words
 * ===========================================================================
 * This used to be a line of five logos with their names beside them, set as a
 * paragraph of links along one edge. It read as a footer. The marks are the
 * most recognisable thing on this part of the page — five brands a visitor
 * already trusts — and setting them at 12px inside running text wasted the one
 * moment where borrowed recognition does the work for us.
 *
 * They are still links, because these are the source pages the SEO
 * architecture hangs off. They are now links you can see.
 *
 * ===========================================================================
 * The extension is a footnote no longer
 * ===========================================================================
 * It has to be mentioned — Etsy, Amazon, AliExpress and TourRadar sellers are
 * real customers — and it used to be mentioned in grey 13px type under a right
 * aligned column, which is where text goes to not be read.
 *
 * It now gets its own band: the four marks it covers, a sentence, and a link.
 * What it must *not* get is equal billing with the five above, because it is a
 * genuinely different mechanism — those five are fetched from a public page by
 * the server, these are collected in your own browser while you are signed in.
 * A page that blurs the two produces a visitor who installs nothing and imports
 * nothing. Hence: same page, clearly separated, visibly secondary — a tinted
 * band rather than a row of white tiles.
 */
export function SourcesStrip({ aside }: { aside?: ReactNode }) {
  return (
    <div>
      {/*
        The heading block used to be a lone `max-w-text` column with the right
        half of a wide screen empty beside it. `aside` fills that — see the
        homepage, which puts one customer's review there.

        A grid rather than a float, and only from `lg`: on anything narrower the
        two stack, which is the right order (the claim, then the evidence for
        it) and needs no rearranging. `items-center` because the aside is a
        small object with its own balance and aligning its top edge to an
        eyebrow leaves it hanging.
      */}
      <div
        className={cx(
          aside &&
            'grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)] lg:items-center lg:gap-16'
        )}
      >
        <div className="max-w-text">
          {/* The hero's badge shape, carrying the marks in miniature. */}
          <p className="inline-flex items-center gap-2.5 rounded-full bg-white py-1 pr-3.5 pl-1 text-caption font-semibold text-ink-600 shadow-[0_1px_2px_rgba(10,10,11,0.04)] ring-1 ring-black/[0.08]">
            <span className="flex -space-x-1.5" aria-hidden>
              {CONNECT_SOURCES.map((source) => (
                <span
                  key={source.id}
                  className="grid size-6 place-items-center rounded-full bg-white ring-2 ring-white shadow-[0_0_0_1px_rgba(10,10,11,0.08)]"
                >
                  <SourceGlyph source={source.id} className="size-3.5" />
                </span>
              ))}
            </span>
            Works with
          </p>
          <Heading level={2} className="mt-5">
            Wherever your reviews already are.
          </Heading>
          <Text className="mt-4 max-w-prose">
            Paste the address of your listing or your app&rsquo;s store page,
            and ReviewsJet fetches what your customers wrote, then keeps
            checking for new ones.
          </Text>
        </div>

        {aside}
      </div>

      {/*
        All seven across on a wide screen, four then three on a tablet, two on a
        phone. Not `flex-wrap`: a wrapping row leaves a lonely last tile on its
        own line at most widths, and a grid at least leaves a deliberate gap.
      */}
      <ul
        onPointerMove={trackSpotlight}
        className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7 lg:gap-3"
      >
        {CONNECT_SOURCES.map((source) => (
          <li key={source.id}>
            <SiteLink
              to={sourcePagePath(source.id)}
              className={cx(
                'spotlight group relative flex h-full flex-col items-center justify-center gap-4 overflow-hidden rounded-[1.75rem] bg-white px-3 pt-9 pb-6 text-center',
                'ring-1 ring-black/[0.06] shadow-[0_1px_2px_rgba(10,10,11,0.04)]',
                'transition-[box-shadow,transform] duration-300 [transition-timing-function:var(--ease-out-soft)]',
                // Lift, not colour. The marks are already coloured; the blue
                // glow follows the cursor rather than filling the tile.
                'hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(20,40,120,0.35)] hover:ring-black/10'
              )}
            >
              <span className="grid size-16 place-items-center rounded-2xl bg-surface ring-1 ring-black/[0.04] transition-transform duration-300 group-hover:scale-105">
                <SourceGlyph source={source.id} className="size-8 shrink-0" />
              </span>
              <span className="text-body font-bold tracking-[-0.01em] text-ink-950">
                {source.name}
              </span>
              <span className="flex items-center gap-1 text-caption font-semibold text-ink-400 transition-colors group-hover:text-brand">
                See how
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="size-3 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                >
                  <path d="M3 8h10M9 4l4 4-4 4" />
                </svg>
              </span>
            </SiteLink>
          </li>
        ))}
      </ul>

      {/* ------------------------------------------------------- Extension */}
      <div
        className={cx(
          'mt-4 overflow-hidden rounded-[1.75rem] bg-white ring-1 ring-black/[0.06] lg:mt-5',
          'shadow-[0_1px_2px_rgba(10,10,11,0.04)] flex flex-col lg:flex-row'
        )}
      >
        {/*
          The Chrome mark, on a tinted panel of its own.

          The band used to say "Chrome extension" in a sentence and show no
          Chrome anywhere, which is the one logo a reader needs here: it answers
          "where does this thing live" before the sentence does, and it is the
          difference between a paragraph about an extension and a thing you can
          see is an extension.
        */}
        <div className="relative flex shrink-0 items-center gap-4 overflow-hidden bg-brand-wash px-6 py-6 lg:flex-col lg:justify-center lg:px-10">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-10 -left-10 size-40 rounded-full bg-brand/15 blur-2xl"
          />
          <span className="relative grid size-16 place-items-center rounded-2xl bg-white shadow-[0_8px_24px_-12px_rgba(20,40,120,0.4)] ring-1 ring-black/[0.04]">
            <ChromeMark className="size-9 shrink-0" />
          </span>
          <p className="relative text-caption font-semibold text-ink-600 lg:text-center">
            Chrome
            <span className="lg:block"> extension</span>
          </p>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-5 px-6 py-7 sm:flex-row sm:items-center sm:gap-8 lg:px-9">
          <div className="min-w-0 flex-1">
            <p className="text-body-lg font-bold tracking-[-0.015em] text-ink-950">
              Selling on{' '}
              {EXTENSION_SOURCES.map((source) => source.name).join(', ')}?
            </p>
            <p className="mt-1.5 max-w-prose text-body-sm text-ink-600">
              These cannot be read from a server, so the extension collects them
              in your own browser while you are signed in to your own account.
            </p>

            {/*
              The four marketplaces, shown rather than only named. Four small
              marks say "these ones" faster than four proper nouns do, and they
              are the same glyphs the rest of the site draws.
            */}
            <ul className="mt-4 flex items-center -space-x-2" aria-hidden>
              {EXTENSION_SOURCES.map((source) => (
                <li
                  key={source.id}
                  className="grid size-9 place-items-center rounded-full bg-white ring-2 ring-white shadow-[0_0_0_1px_rgba(10,10,11,0.08)]"
                >
                  <SourceGlyph source={source.id} className="size-4" />
                </li>
              ))}
            </ul>
          </div>

          <a
            href={CHROME_EXTENSION_URL}
            target="_blank"
            rel="noreferrer"
            className={cx(
              'group inline-flex h-11 shrink-0 items-center gap-2 self-start rounded-full pr-1.5 pl-5',
              'text-body-sm font-semibold text-white bg-ink-950',
              'transition-colors duration-[var(--duration-fast)]',
              'hover:bg-ink-800 sm:self-center'
            )}
          >
            Get the extension
            <span
              aria-hidden
              className="grid size-8 place-items-center rounded-full bg-white/15 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            >
              <svg
                viewBox="0 0 16 16"
                className="size-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 3h7v7M13 3 4 12" />
              </svg>
            </span>
            <span className="sr-only">(opens the Chrome Web Store)</span>
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Feeds the cursor position to the tile under it, for the `.spotlight` glow.
 * One handler on the list rather than one per tile.
 */
function trackSpotlight(event: PointerEvent<HTMLUListElement>) {
  const tile = (event.target as Element).closest<HTMLElement>('.spotlight');
  if (!tile) return;
  const rect = tile.getBoundingClientRect();
  tile.style.setProperty('--spot-x', `${event.clientX - rect.left}px`);
  tile.style.setProperty('--spot-y', `${event.clientY - rect.top}px`);
}

/**
 * The Chrome mark.
 *
 * Drawn here rather than added to the shared package's `SOURCE_MARKS`, which is
 * a list of *review sources* — Chrome is a browser, and putting it in there
 * would offer it to every component that maps over that record, including the
 * ones that draw a source badge on a review card.
 *
 * Constructed geometrically rather than copied: three 120° arcs meeting at the
 * centre, boundaries at 180°, 300° and 60°, with the blue hub and its white
 * surround drawn over the top to cut them. That is the actual construction of
 * the logo, so it stays true at any size instead of being a traced approximation
 * that goes wrong when scaled.
 */
function ChromeMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden focusable="false">
      {/* Top — red */}
      <path fill="#EA4335" d="M1 12 A11 11 0 0 1 17.5 2.474 L12 12 Z" />
      {/* Right — yellow */}
      <path fill="#FBBC05" d="M17.5 2.474 A11 11 0 0 1 17.5 21.526 L12 12 Z" />
      {/* Lower left — green */}
      <path fill="#34A853" d="M17.5 21.526 A11 11 0 0 1 1 12 L12 12 Z" />
      {/* The hub, and the white gap that separates it from the segments. */}
      <circle cx="12" cy="12" r="5.6" fill="#fff" />
      <circle cx="12" cy="12" r="4.5" fill="#4285F4" />
    </svg>
  );
}
